import React, { useState, useEffect } from "react";
import PrescribedSection from "./PrescribedSection";
import PrescriptionSection from "./PrescriptionSection";
import AdditionalMedicationsSection from "./AdditionalMedicationsSection";
import { ClinicalServiceTypes } from "../../../text/data";
import { getTreatmentDrugs } from "../../service-steps/treatment/data";

const StandardMedicationView = ({
  filteredMedication,
  onMedicationClick,
  onMenuAction,
  onExpandMedication,
  expandedMedications,
  hoveredMed,
  onMouseEnter,
  onMouseLeave,
  menuMedication,
  onMenuOpen,
  showMenu,
  menuData,
  selectedMedication,
  isMatchingDrug,
  medicationDetails,
  getNoRecordMessage,
  isIntervention,
  clinicalServiceObj,
  clinicalServiceName,
  expandedSection: externalExpandedSection,
  onExpandedSectionChange,
  prescribedRxEnabled,
  activeTab,
}) => {
  // Get treatment drugs first
  const treatment = clinicalServiceObj?.initialAssessment?.initialAssessmentForm?.treatment;
  const treatmentDrugs = treatment ? getTreatmentDrugs(treatment) : [];
  
  // Determine if prescribedRx section should be shown and expanded by default
  const shouldShowPrescribedRx = (() => {
    if (clinicalServiceName === ClinicalServiceTypes.AllergicRhinitis) {
      // Inside Allergic Rhinitis: show only if there are treatment drugs OR prescribedRxEnabled is true
      return treatmentDrugs.length > 0 || prescribedRxEnabled;
    } else {
      // Outside Allergic Rhinitis: show only if prescribedRxEnabled is true
      return prescribedRxEnabled;
    }
  })();
  
  // Special handling for pending tab - only show prescribedRx section
  const isPendingTab = activeTab?.name === "pending";
  
  // Determine initial expanded section with priority for Allergic Rhinitis:
  // 1. If expandedSection is passed = use passed section (highest priority)
  // 2. If treatment drugs exist = expand prescribedRx
  // 3. No expandedSection + no treatment drugs = default (prescription)
  const getInitialExpandedSection = () => {
    if (clinicalServiceName === ClinicalServiceTypes.AllergicRhinitis) {
      // Priority 1: If expandedSection is passed, use it (highest priority)
      if (externalExpandedSection) {
        return externalExpandedSection;
      }
      // Priority 2: If treatment drugs exist, expand prescribedRx
      if (treatmentDrugs.length > 0) {
        return 'prescribedRx';
      }
      // Priority 3: No expandedSection + no treatment drugs = default
      return 'prescription';
    } else {
      // Outside Allergic Rhinitis - use prescribedRxEnabled logic
      if (prescribedRxEnabled) {
        return 'prescribedRx';
      }
      return 'prescription';
    }
  };
  
  // Manage expanded section locally; sync from external when provided
  const [expandedSection, setExpandedSection] = React.useState(getInitialExpandedSection());
  React.useEffect(() => {
    // Sync from external prop (including null to allow closing)
    if (externalExpandedSection !== undefined) {
      setExpandedSection(externalExpandedSection);
    }
  }, [externalExpandedSection]);

  // Accordion handlers for sections
  const handlePrescribedRxToggle = () => {
    if (expandedSection === 'prescribedRx') {
      // Allow closing - set to null to close all sections
      setExpandedSection(null);
      onExpandedSectionChange && onExpandedSectionChange(null);
    } else {
      setExpandedSection('prescribedRx');
      onExpandedSectionChange && onExpandedSectionChange('prescribedRx');
    }
  };

  const handlePrescriptionToggle = () => {
    if (expandedSection === 'prescription') {
      // Allow closing - set to null to close all sections
      setExpandedSection(null);
      onExpandedSectionChange && onExpandedSectionChange(null);
    } else {
      setExpandedSection('prescription');
      onExpandedSectionChange && onExpandedSectionChange('prescription');
    }
  };

  const handleAdditionalToggle = () => {
    if (expandedSection === 'additional') {
      // Allow closing - set to null to close all sections
      setExpandedSection(null);
      onExpandedSectionChange && onExpandedSectionChange(null);
    } else {
      setExpandedSection('additional');
      onExpandedSectionChange && onExpandedSectionChange('additional');
    }
  };

  // Render sections in order with expanded section at the end
  const renderMedicationSections = () => {
    const sections = [];
    
    // Special handling for pending tab - only show prescribedRx section
    if (isPendingTab) {
      sections.push(
        <PrescribedSection
          key="prescribedRx-expanded-pending"
          filteredMedication={filteredMedication}
          showPrescribedRxMeds={expandedSection === 'prescribedRx'}
          setShowPrescribedRxMeds={handlePrescribedRxToggle}
          onMedicationClick={onMedicationClick}
          onMenuAction={onMenuAction}
          onExpandMedication={onExpandMedication}
          expandedMedications={expandedMedications}
          hoveredMed={hoveredMed}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          menuMedication={menuMedication}
          onMenuOpen={onMenuOpen}
          showMenu={showMenu}
          menuData={menuData}
          selectedMedication={selectedMedication}
          isMatchingDrug={isMatchingDrug}
          medicationDetails={medicationDetails}
          getNoRecordMessage={getNoRecordMessage}
          isIntervention={isIntervention}
          clinicalServiceName={clinicalServiceName}
          prescribedRxEnabled={prescribedRxEnabled}
        />
      );
      return sections;
    }
    
    // Add collapsed sections first - only show prescribedRx if it should be shown
    if (shouldShowPrescribedRx && expandedSection !== 'prescribedRx') {
      sections.push(
        <PrescribedSection
          key="prescribedRx-collapsed"
          filteredMedication={filteredMedication}
          showPrescribedRxMeds={false}
          setShowPrescribedRxMeds={handlePrescribedRxToggle}
          onMedicationClick={onMedicationClick}
          onMenuAction={onMenuAction}
          onExpandMedication={onExpandMedication}
          expandedMedications={expandedMedications}
          hoveredMed={hoveredMed}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          menuMedication={menuMedication}
          onMenuOpen={onMenuOpen}
          showMenu={showMenu}
          menuData={menuData}
          selectedMedication={selectedMedication}
          isMatchingDrug={isMatchingDrug}
          medicationDetails={medicationDetails}
          getNoRecordMessage={getNoRecordMessage}
          isIntervention={isIntervention}
          clinicalServiceName={clinicalServiceName}
          prescribedRxEnabled={prescribedRxEnabled}
        />
      );
    }
    
    if (expandedSection !== 'prescription') {
      sections.push(
        <PrescriptionSection
          key="prescription-collapsed"
          filteredMedication={filteredMedication}
          showPrescriptionMeds={false}
          setShowPrescriptionMeds={handlePrescriptionToggle}
          onMedicationClick={onMedicationClick}
          onMenuAction={onMenuAction}
          onExpandMedication={onExpandMedication}
          expandedMedications={expandedMedications}
          hoveredMed={hoveredMed}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          menuMedication={menuMedication}
          onMenuOpen={onMenuOpen}
          showMenu={showMenu}
          menuData={menuData}
          selectedMedication={selectedMedication}
          isMatchingDrug={isMatchingDrug}
          medicationDetails={medicationDetails}
          getNoRecordMessage={getNoRecordMessage}
          isIntervention={isIntervention}
        />
      );
    }
    
    if (expandedSection !== 'additional') {
      sections.push(
        <AdditionalMedicationsSection
          key="additional-collapsed"
          filteredMedication={filteredMedication}
          showAdditionalMeds={false}
          setShowAdditionalMeds={handleAdditionalToggle}
          onMedicationClick={onMedicationClick}
          onMenuAction={onMenuAction}
          onExpandMedication={onExpandMedication}
          expandedMedications={expandedMedications}
          hoveredMed={hoveredMed}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          menuMedication={menuMedication}
          onMenuOpen={onMenuOpen}
          showMenu={showMenu}
          menuData={menuData}
          selectedMedication={selectedMedication}
          isMatchingDrug={isMatchingDrug}
          medicationDetails={medicationDetails}
          getNoRecordMessage={getNoRecordMessage}
          isIntervention={isIntervention}
        />
      );
    }
    
    // Add expanded section at the end
    if (shouldShowPrescribedRx && expandedSection === 'prescribedRx') {
      sections.push(
        <PrescribedSection
          key="prescribedRx-expanded"
          filteredMedication={filteredMedication}
          showPrescribedRxMeds={true}
          setShowPrescribedRxMeds={handlePrescribedRxToggle}
          onMedicationClick={onMedicationClick}
          onMenuAction={onMenuAction}
          onExpandMedication={onExpandMedication}
          expandedMedications={expandedMedications}
          hoveredMed={hoveredMed}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          menuMedication={menuMedication}
          onMenuOpen={onMenuOpen}
          showMenu={showMenu}
          menuData={menuData}
          selectedMedication={selectedMedication}
          isMatchingDrug={isMatchingDrug}
          medicationDetails={medicationDetails}
          getNoRecordMessage={getNoRecordMessage}
          isIntervention={isIntervention}
          clinicalServiceName={clinicalServiceName}
          prescribedRxEnabled={prescribedRxEnabled}
        />
      );
    } else if (expandedSection === 'prescription') {
      sections.push(
        <PrescriptionSection
          key="prescription-expanded"
          filteredMedication={filteredMedication}
          showPrescriptionMeds={true}
          setShowPrescriptionMeds={handlePrescriptionToggle}
          onMedicationClick={onMedicationClick}
          onMenuAction={onMenuAction}
          onExpandMedication={onExpandMedication}
          expandedMedications={expandedMedications}
          hoveredMed={hoveredMed}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          menuMedication={menuMedication}
          onMenuOpen={onMenuOpen}
          showMenu={showMenu}
          menuData={menuData}
          selectedMedication={selectedMedication}
          isMatchingDrug={isMatchingDrug}
          medicationDetails={medicationDetails}
          getNoRecordMessage={getNoRecordMessage}
          isIntervention={isIntervention}
        />
      );
    } else if (expandedSection === 'additional') {
      sections.push(
        <AdditionalMedicationsSection
          key="additional-expanded"
          filteredMedication={filteredMedication}
          showAdditionalMeds={true}
          setShowAdditionalMeds={handleAdditionalToggle}
          onMedicationClick={onMedicationClick}
          onMenuAction={onMenuAction}
          onExpandMedication={onExpandMedication}
          expandedMedications={expandedMedications}
          hoveredMed={hoveredMed}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          menuMedication={menuMedication}
          onMenuOpen={onMenuOpen}
          showMenu={showMenu}
          menuData={menuData}
          selectedMedication={selectedMedication}
          isMatchingDrug={isMatchingDrug}
          medicationDetails={medicationDetails}
          getNoRecordMessage={getNoRecordMessage}
          isIntervention={isIntervention}
        />
      );
    }
    
    return sections;
  };

  return <>{renderMedicationSections()}</>;
};

export default StandardMedicationView;
